/**
 * Field Configuration TypeScript types for the Universal CRM Table system
 */

import type { FieldValidationConfig } from "./base-types";
import type {
  ArrayConfig,
  ClientSelectConfig,
  CompanySelectConfig,
  DateWorkedConfig,
  DueDateConfig,
  FieldDataLineageConfig,
  FieldOption,
  FieldType,
  FilterType,
  ForeignKeyConfig,
  MultiUserConfig,
  TagsConfig,
  TemplateSelectConfig,
} from "./field-config-subtypes";

export type {
  ArrayConfig,
  ClientSelectConfig,
  CompanySelectConfig,
  DateWorkedConfig,
  DueDateConfig,
  ExtendedSortDirection,
  FieldDataLineageConfig,
  FieldOption,
  FieldType,
  FilterType,
  ForeignKeyAction,
  ForeignKeyConfig,
  MultiUserConfig,
  SortDirection,
  TagsConfig,
  TemplateSelectConfig,
  TypedFieldValue,
} from "./field-config-subtypes";

/**
 * Core field configuration interface
 */
export interface SimpleFieldConfig {
  type?: FieldType;
  label: string;
  isStatusField?: boolean;
  isForeignKey?: boolean;
  foreignKey?: ForeignKeyConfig;
  isReadOnlyDerived?: boolean;
  readOnlyReason?: string;
  dataLineage?: FieldDataLineageConfig;
  formOnly?: boolean;
  autoFill?: boolean;
  sortable?: boolean;
  filterable?: boolean;
  searchable?: boolean;
  resizable?: boolean;
  hideable?: boolean;
  editable?: boolean;
  bulkEditable?: boolean;
  width?: number;
  minWidth?: number;
  maxWidth?: number;
  options?: FieldOption[];
  filterType?: FilterType;
  filterOptions?: Array<{ label: string; value: string | number | boolean }>;
  min?: number;
  max?: number;
  placeholder?: string;
  helpText?: string;
  primary?: boolean;
  badge?: boolean;
  mobileDetailSectionId?: string;
  required?: boolean;
  validation?: FieldValidationConfig;
  excludeFromApi?: boolean;
  clientSelectConfig?: ClientSelectConfig;
  companySelectConfig?: CompanySelectConfig;
  ticketSelectConfig?: { showModal?: boolean; limit?: number };
  multiUserConfig?: MultiUserConfig;
  tagsConfig?: TagsConfig;
  dueDateConfig?: DueDateConfig;
  dateWorkedConfig?: DateWorkedConfig;
  templateSelectConfig?: TemplateSelectConfig;
  arrayConfig?: ArrayConfig;
  customComponent?: string;
  icon?: string;
  groupable?: boolean;
  groupingOptions?: {
    dateGranularity?: "day" | "month" | "year";
    numberRanges?: {
      ranges: Array<{ min: number; max: number | null; label: string }>;
    };
  };
  datetimePresets?: {
    enabled: boolean;
    presets?: string[];
    showCustomRange?: boolean;
  };
  allowedRoles?: string[];
  formSpan?: 1 | 2 | 3;
  lineItemsType?: "staff" | "hard-cost";
  showRowActions?: boolean;
  showOnCard?: boolean;
  cardSpan?: 1 | 2 | 3;
  engagementStaleness?: boolean;
  /**
   * How a `type: "number"` value is DISPLAYED. Without this, numbers fell
   * through the cell renderer to `String(value)` — so a $25,000 deal read
   * "25000" in tables, cards, and the pipeline board.
   *
   * Left undefined, a number keeps rendering verbatim. That default is
   * deliberate: identifier-ish numbers (`ticket_number`) must NOT be grouped
   * into "10,500", so money/percent formatting is opt-in per field rather than
   * guessed from the value.
   *
   * - `currency` → `$25,000` (USD, no cents unless the value has them)
   * - `percent`  → `40%`
   * - `decimal`  → `1,234.5` (grouped, up to 2 decimals)
   * - `integer`  → `1,234` (grouped, rounded)
   */
  numberFormat?: "currency" | "percent" | "decimal" | "integer";
  /**
   * Optional declarative hint of which GraphQL field this column maps to.
   * Used for documentation/grep-ability so a field declaration is self-contained.
   * Runtime GraphQL selection is still handled by config-enhancers.
   */
  graphqlSelector?: string;
  /**
   * Optional list of saved-view ids that include this field in `visibleColumns`
   * by default. Lets each field declaration document its own saved-view membership
   * so saved-view definitions can be derived from field declarations.
   */
  savedViewMembership?: readonly string[];
  /**
   * Optional per-field GraphQL filter translator.
   *
   * When the filter-conversion service encounters a value for this field, it
   * will call this hook BEFORE applying any generic translation (eq/in/range/
   * datetime-preset). If the hook returns a non-null record, those entries are
   * merged into the GraphQL filter object and the generic path is skipped.
   *
   * This lets entity-specific filter shapes (e.g. `due_date: "overdue"` for
   * tasks/tickets, which expands to `{ due_date: { lt: now }, status: { neq:
   * "completed" } }`) live next to the field declaration instead of being
   * branched on `entityName` inside `filter-conversion.ts`.
   *
   * Return `null` to fall through to the generic conversion path.
   */
  toGraphQLFilter?: (
    value: unknown,
    context: FieldFilterContext,
  ) => Record<string, unknown> | null;
}

/**
 * Context passed to `SimpleFieldConfig.toGraphQLFilter` hooks.
 *
 * - `now` — the current time, threaded in so date-relative filters
 *   (e.g. "overdue") produce deterministic output and are easier to test.
 * - `userId` — the current user's id, for filters that key off "mine" /
 *   ownership without requiring a separate auth hook inside the field hook.
 *   Optional because most field hooks don't need it.
 */
export interface FieldFilterContext {
  now: Date;
  userId?: string;
}

type IsUnion<T, U = T> = T extends T ? ([U] extends [T] ? false : true) : never;

type LabelToKeys<T extends Record<string, { label: string }>> = {
  [K in keyof T as T[K]["label"]]: K;
};

type DuplicateFieldLabelKeys<T extends Record<string, { label: string }>> = {
  [L in keyof LabelToKeys<T>]: IsUnion<LabelToKeys<T>[L]> extends true ? LabelToKeys<T>[L] : never;
}[keyof LabelToKeys<T>];

export type EnsureUniqueFieldLabels<T extends Record<string, { label: string }>> =
  [DuplicateFieldLabelKeys<T>] extends [never] ? T : never;

export const defineUniqueFieldConfigMap = <T extends Record<string, SimpleFieldConfig>>(
  fields: EnsureUniqueFieldLabels<T>
): T => fields;

/**
 * Columns the database manages itself: they default to `now()` and are NOT NULL
 * on `deals` (and both `contracts` timestamps). A create/edit form must NEVER
 * write them — an explicit value, or the `null` the form-data cleaner coerces an
 * empty date field to, OVERRIDES the DB default and fails the NOT NULL
 * constraint ("null value in column \"created_at\" ... violates not-null").
 *
 * Keep in sync with `DB_MANAGED_TIMESTAMP_FIELDS` in
 * `components/shared/EnhancedGenericCreateForm/form-data-cleaner.ts` (the runtime
 * strip). This type is the compile-time half of that guard.
 */
export type DbManagedFieldKey = "created_at" | "updated_at";

/**
 * Compile-time guard: every DB-managed timestamp field that appears in a
 * field-config map MUST be declared `excludeFromApi: true`, so it is stripped
 * from write payloads. A violating field resolves to `never`, so the offending
 * object literal at the `defineFieldConfigMap` call site fails to typecheck
 * (same mechanism as `EnsureUniqueFieldLabels`). This makes the original
 * `deals.created_at` NOT NULL crash impossible to reintroduce accidentally —
 * adding `created_at`/`updated_at` to a config without `excludeFromApi: true`
 * no longer compiles.
 */
type DbManagedViolationKeys<T extends Record<string, SimpleFieldConfig>> = {
  [K in keyof T]-?: K extends DbManagedFieldKey
    ? T[K]["excludeFromApi"] extends true
      ? never
      : K
    : never;
}[keyof T];

export type EnsureDbManagedFieldsExcluded<T extends Record<string, SimpleFieldConfig>> =
  [DbManagedViolationKeys<T>] extends [never] ? T : never;

/**
 * Define a partial field config map while preserving literal key and label types.
 *
 * Use this on per-section field bundles (e.g. `coreDealsFields`, `companyContactFields`)
 * so the literal keys propagate up to the entity config and into
 * `keyof typeof ENTITY_FIELDS`. This is what powers the strongly-typed
 * `SavedViewConfig<EntityFieldId>` pattern.
 *
 * The constraint preserves nested literal types (notably `label`) which
 * `defineUniqueFieldConfigMap` later relies on for duplicate-label detection.
 *
 * Prefer this over `: Record<string, SimpleFieldConfig> = {...}` (loses keys) and over
 * `... satisfies Record<string, SimpleFieldConfig>` (widens `label` to `string`).
 */
export const defineFieldConfigMap = <
  const T extends Readonly<Record<keyof T, SimpleFieldConfig>>
>(
  // `EnsureDbManagedFieldsExcluded<T>` resolves to `T` normally and to `never`
  // when a DB-managed timestamp field is missing `excludeFromApi: true`, so the
  // offending call fails to typecheck (same mechanism as EnsureUniqueFieldLabels).
  fields: EnsureDbManagedFieldsExcluded<T>
): T => fields;
